import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk-service';
import { Country } from '~/Api/Country';
import { ReferenceBasketVersion } from '~/Api/SkPriceReferencing/MaxPrice';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import * as SkMaxPriceApi from '~/Api/User/ProductGroup/SkMaxPrice';
import { AppError, handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	Action as PriceReferencingAction,
	fetchReferenceBasketVersions,
	Module,
	pruneReferenceBasketVersions,
	selectReferenceBasketVersions,
} from '~/PriceReferencing';
import { PricedProduct, Product } from '~/Product';
import { Action as ProductDatasetAction, pruneDatasets } from '~/ProductDataset';
import { Action as ProductPriceAction, pruneAllPrices } from '~/ProductPrice';
import { State, ThunkAction } from '~/Store';

import { Action } from './Action';
import {
	commitSkMaxPriceUpdate,
	fetchSkMaxPrice,
	handleCommitError,
	handleSkMaxPriceUpdateResponse,
	pruneSkMaxPrice,
	scheduleSkMaxPriceUpdate,
} from './actions';
import { selectExport, selectSkMaxPrice } from './reducer';
import SkMaxPrice from './SkMaxPrice';

interface BasketProduct {
	country: Country;
	product: SkMaxPriceApi.Product;
	usedForCalculation: boolean;
}
interface BasketOptionalProduct {
	country: Country;
	product: SkMaxPriceApi.Product | null;
	usedForCalculation: boolean;
}

const containsCountry = (countries: Country[], code: string): boolean =>
	countries.find((country) => country.alpha2 === code) !== undefined;

const compareReferences = (
	reference: BasketProduct | BasketOptionalProduct,
	otherReference: BasketProduct | BasketOptionalProduct
): number => {
	if (reference.product !== null && otherReference.product === null) {
		return -1;
	}
	if (reference.product === null && otherReference.product !== null) {
		return 1;
	}
	const comparison = reference.country.alpha2.localeCompare(otherReference.country.alpha2);
	if (comparison !== 0) {
		return comparison;
	}
	if (reference.product !== null && otherReference.product !== null) {
		return reference.product.product.brandName.localeCompare(otherReference.product.product.brandName);
	}
	return 0;
};

const createSections = memoizeOne(
	(skMaxPrice: SkMaxPriceApi.SkMaxPrice | null, versions: ReferenceBasketVersion[] | null, showAll: boolean) => {
		const version =
			versions !== null && skMaxPrice !== null
				? versions.find((version) => version.id === skMaxPrice.reference.referenceBasketVersion) ?? null
				: null;

		const referenceCountries = version !== null ? version.referenceCountries : [];
		const products = skMaxPrice !== null && version !== null ? skMaxPrice.products : [];
		const referenceCountryCodes = new Set(referenceCountries?.map((country) => country.alpha2));
		const nonsetCountryCodes = new Set(referenceCountryCodes);
		const usedForCalculationIds = new Set(
			skMaxPrice?.finalCalculation?.threeLowestPricesAvg?.fromProducts?.map((product) => product.id)
		);

		const slovakiaProducts: BasketOptionalProduct[] = [];
		const referencedProducts: BasketOptionalProduct[] = [];
		const otherProducts: BasketProduct[] = [];
		let alternativeProductsCount = 0;

		products.forEach((product) => {
			const { country } = product.product.dataset.source;
			alternativeProductsCount += product.selected ? 0 : 1;

			if (!showAll && !product.selected) {
				return;
			}

			if (country.alpha2 === 'SK') {
				slovakiaProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
				return;
			}
			if (containsCountry(referenceCountries ?? [], country.alpha2)) {
				referencedProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
				nonsetCountryCodes.delete(country.alpha2);
				return;
			}
			if (showAll) {
				otherProducts.push({
					country,
					product,
					usedForCalculation: usedForCalculationIds.has(product.product.id),
				});
			}
		});

		if (slovakiaProducts.length === 0) {
			slovakiaProducts.push({
				country: new Country('SK', 'Slovakia'),
				product: null,
				usedForCalculation: false,
			});
		}

		referenceCountries?.forEach((country) => {
			if (nonsetCountryCodes.has(country.alpha2)) {
				referencedProducts.push({
					country,
					product: null,
					usedForCalculation: false,
				});
			}
		});

		slovakiaProducts.sort(compareReferences);
		referencedProducts.sort(compareReferences);
		otherProducts.sort(compareReferences);

		const basketProducts = [...slovakiaProducts, ...referencedProducts, ...otherProducts].filter(
			(product) => product.product !== null
		) as BasketProduct[];
		const pricedProducts = basketProducts.map(
			(product) => new PricedProduct(product.product, product.product.product, null)
		);

		return {
			slovakiaProducts,
			otherProducts,
			pricedProducts,
			alternativeProductsCount,
			referenceBasketVersion: version,
			referencedProducts,
		};
	}
);

function mapStateToProps(state: State, { group }: { group: UserProductGroup }) {
	const skMaxPriceState = selectSkMaxPrice(state.skMaxPrice, group.id);
	const skMaxPrice = skMaxPriceState?.skMaxPrice ?? null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.skMaxPrice
	);

	return {
		...createSections(skMaxPrice, versions, state.products.showAllProducts),
		skMaxPrice,
		validationResult: selectExport(state.skMaxPrice, group.id)?.validationResult ?? null,
		isFetching: Boolean(skMaxPriceState?.isFetching),
	};
}
type PruneAction = Action | PriceReferencingAction | ProductPriceAction | ProductDatasetAction;
type Dispatch = ThunkDispatch<State, never, Action>;

const actions = {
	onFetch:
		(group: UserProductGroup): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			dispatch(fetchReferenceBasketVersions(Module.skMaxPrice));

			try {
				await dispatch(commitSkMaxPriceUpdate(group.id));
			} catch (error: unknown) {
				dispatch(handleError(error as AppError));
			}

			try {
				await dispatch(fetchSkMaxPrice(group.id));
			} catch (error) {
				if (!(error instanceof SkMaxPriceApi.ErrorResponse)) {
					dispatch(handleError(error));
					return;
				}
				error.handleProductGroupNotFound();
				dispatch(handleError(error));
			}
		},
	onPrune:
		(group: UserProductGroup): ThunkAction<Promise<void>, PruneAction> =>
		async (dispatch) => {
			try {
				await dispatch(commitSkMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}
			dispatch(pruneSkMaxPrice(group.id));
			dispatch(pruneReferenceBasketVersions());
			dispatch(pruneAllPrices());
			dispatch(pruneDatasets());
		},
	onUpdate:
		(group: UserProductGroup, update: SkMaxPriceApi.Update): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			try {
				await dispatch(scheduleSkMaxPriceUpdate(group.id, update));
			} catch (error) {
				dispatch(handleSkMaxPriceUpdateResponse(error, group.id));
			}
		},
	onSave:
		(group: UserProductGroup): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			try {
				await dispatch(commitSkMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleSkMaxPriceUpdateResponse(error, group.id));
			}
		},
	onDeleteItems:
		(group: UserProductGroup, products: Product[], onSuccess: () => void) => async (dispatch: Dispatch) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(SkMaxPrice);
